import React, { useState } from "react";
import { LineChart } from "@/components/LineChart";
import {
  useCommitScoreTimeSeries,
  PredictionIndexEnum,
} from "@/hooks/useCommitScoreTimeSeries";
import {
  useRISEPredictiveScoreTimeSeries,
  RisePredictiveModelEnum,
  RisePredictiveIndexEnum,
} from "@/hooks/useRISEPredictiveScoreTimeSeries";
import { LineChartDataPoint, TooltipData } from "@/components/LineChart/types";
import { useTranslation } from "react-i18next";
import {
  FileDown,
  Share,
  UserRoundCheck,
  TrendingUp,
  BadgeCheck,
} from "lucide-react";
import { exportChartCSV } from "@/util/exportChartCSV";
import { useShareLink } from "@/hooks/useShareLink";
import { standardFormatDateLong } from "@/util/stringUtils";
import { isInDarkMode } from "@/util/themingUtils";
import { toast } from "sonner";
import { Student } from "@/types/Student";
import { Card, CardContent } from "@/components/ui/Card";
import { clsx } from "clsx";
import { useOrgPermissions } from "@/hooks/useOrgPermissions";
import { PartnerFeatureEnum } from "@/__generated__/types";
import { FeatureDialog } from "@/components/FeatureDialog";
import { RiseStar } from "@/assets/svg/RiseStar";

interface PredictiveScoreSectionProps {
  orgId: string;
  student: Student;
}

const INDEX_CONFIG = {
  HIGH: {
    colorClass: "text-green-600",
    translationKey: "studentCard.predictiveScoreGraphs.indexes.high",
  },
  MEDIUM_HIGH: {
    colorClass: "text-purple-600",
    translationKey: "studentCard.predictiveScoreGraphs.indexes.mediumHigh",
  },
  MEDIUM: {
    colorClass: "text-purple-600",
    translationKey: "studentCard.predictiveScoreGraphs.indexes.medium",
  },
  MEDIUM_LOW: {
    colorClass: "text-purple-600",
    translationKey: "studentCard.predictiveScoreGraphs.indexes.mediumLow",
  },
  LOW: {
    colorClass: "text-orange-600",
    translationKey: "studentCard.predictiveScoreGraphs.indexes.low",
  },
} as const;

const getIndexConfig = (
  index: PredictionIndexEnum | RisePredictiveIndexEnum | null,
) => {
  return index && INDEX_CONFIG[index as keyof typeof INDEX_CONFIG]
    ? INDEX_CONFIG[index as keyof typeof INDEX_CONFIG]
    : { colorClass: "text-foreground-medium", translationKey: "" };
};

export const PredictiveScoreSection: React.FC<PredictiveScoreSectionProps> = ({
  orgId,
  student,
}) => {
  const { t } = useTranslation();
  const { shareCurrentLink } = useShareLink();
  const { hasOrgFeature } = useOrgPermissions();
  const [selectedButton, setSelectedButton] = useState<
    "apply" | "commit" | "yield" | "melt"
  >("commit");
  const [showFeatureDialog, setShowFeatureDialog] = useState(false);

  const hasRISE = hasOrgFeature(PartnerFeatureEnum.Rise);

  const {
    timeSeries: commitTimeSeries,
    loading: commitLoading,
    error: commitError,
  } = useCommitScoreTimeSeries(
    !hasRISE ? orgId : "",
    !hasRISE ? student?.id || "" : "",
  );

  const {
    timeSeries: riseTimeSeries,
    loading: riseLoading,
    error: riseError,
  } = useRISEPredictiveScoreTimeSeries(
    selectedButton.toUpperCase() as RisePredictiveModelEnum,
    hasRISE ? orgId : undefined,
    hasRISE ? student?.id : undefined,
  );

  const getActiveData = () => {
    if (!hasRISE) {
      return {
        timeSeries: commitTimeSeries,
        loading: commitLoading,
        error: commitError,
      };
    }

    return {
      timeSeries: riseTimeSeries,
      loading: riseLoading,
      error: riseError,
    };
  };

  const { timeSeries, loading, error } = getActiveData();

  const chartData: LineChartDataPoint[] = timeSeries.map((point) => ({
    date: point.date,
    value: point.value ?? 0,
  }));

  const predictiveButtons = [
    {
      key: "apply" as const,
      titleKey: "studentCard.predictiveScoreGraphs.cardButtons.apply",
      icon: UserRoundCheck,
      iconStyles: "border border-indigo-600 bg-indigo-50",
      value: hasRISE
        ? student?.studentOrganizationStatus?.rise?.applyScore
        : null,
      index: hasRISE
        ? student?.studentOrganizationStatus?.rise?.applyIndex
        : null,
    },
    {
      key: "commit" as const,
      titleKey: "studentCard.predictiveScoreGraphs.cardButtons.commit",
      icon: TrendingUp,
      iconStyles: "border border-yellow-600 bg-yellow-50",
      value: hasRISE
        ? student?.studentOrganizationStatus?.rise?.commitScore
        : student?.studentOrganizationStatus?.commitScore,
      index: hasRISE
        ? student?.studentOrganizationStatus?.rise?.commitIndex
        : student?.studentOrganizationStatus?.commitIndex,
    },
    {
      key: "melt" as const,
      titleKey: "studentCard.predictiveScoreGraphs.cardButtons.melt",
      icon: BadgeCheck,
      iconStyles: "border border-emerald-600 bg-emerald-50",
      value: hasRISE
        ? student?.studentOrganizationStatus?.rise?.meltScore
        : null,
      index: hasRISE
        ? student?.studentOrganizationStatus?.rise?.meltIndex
        : null,
    },
    {
      key: "yield" as const,
      titleKey: "studentCard.predictiveScoreGraphs.cardButtons.yield",
      icon: TrendingUp,
      iconStyles: "border border-yellow-600 bg-yellow-50",
      value: hasRISE
        ? student?.studentOrganizationStatus?.rise?.yieldScore
        : null,
      index: hasRISE
        ? student?.studentOrganizationStatus?.rise?.yieldIndex
        : null,
    },
  ];

  const getGraphTitleAndSubtitle = () => {
    if (!hasRISE) {
      return {
        title: t("studentCard.predictiveScoreGraphs.commitScoreGraph.title"),
        subtitle: t(
          "studentCard.predictiveScoreGraphs.commitScoreGraph.subtitle",
        ),
      };
    }

    switch (selectedButton) {
      case "apply":
        return {
          title: t("studentCard.predictiveScoreGraphs.riseApplyGraph.title"),
          subtitle: t(
            "studentCard.predictiveScoreGraphs.riseApplyGraph.subtitle",
          ),
        };
      case "commit":
        return {
          title: t("studentCard.predictiveScoreGraphs.riseCommitGraph.title"),
          subtitle: t(
            "studentCard.predictiveScoreGraphs.riseCommitGraph.subtitle",
          ),
        };
      case "yield":
        return {
          title: t("studentCard.predictiveScoreGraphs.riseYieldGraph.title"),
          subtitle: t(
            "studentCard.predictiveScoreGraphs.riseYieldGraph.subtitle",
          ),
        };
      case "melt":
        return {
          title: t("studentCard.predictiveScoreGraphs.riseMeltGraph.title"),
          subtitle: t(
            "studentCard.predictiveScoreGraphs.riseMeltGraph.subtitle",
          ),
        };
      default:
        return {
          title: t("studentCard.predictiveScoreGraphs.commitScoreGraph.title"),
          subtitle: t(
            "studentCard.predictiveScoreGraphs.commitScoreGraph.subtitle",
          ),
        };
    }
  };

  const handleButtonClick = (
    buttonKey: "apply" | "commit" | "yield" | "melt",
  ) => {
    if (!hasRISE && buttonKey !== "commit") {
      setShowFeatureDialog(true);
      return;
    }

    setSelectedButton(buttonKey);
  };

  const handleExportCSV = () => {
    try {
      const exportData = chartData.map((point) => {
        const convertedValue =
          point.value !== null && point.value !== undefined
            ? point.value
            : null;
        return {
          [t(
            "studentCard.predictiveScoreGraphs.export.headers.date",
          ).toLowerCase()]: point.date,
          [t(
            "studentCard.predictiveScoreGraphs.export.headers.score",
          ).toLowerCase()]: convertedValue,
        };
      });

      exportChartCSV({
        filename: "commit-score-data",
        headers: [
          t("studentCard.predictiveScoreGraphs.export.headers.date"),
          t("studentCard.predictiveScoreGraphs.export.headers.score"),
        ],
        data: exportData,
        dateFormat: "locale",
      });
      toast.success(
        t("studentCard.predictiveScoreGraphs.export.successMessage"),
      );
    } catch (error) {
      console.error("Failed to export CSV:", error);
      toast.error(t("studentCard.predictiveScoreGraphs.export.failureMessage"));
    }
  };

  const actionsDisabled = loading || Boolean(error) || chartData.length === 0;

  const getSectionTitle = (title: string): React.ReactNode => {
    return hasRISE ? (
      <span className="flex items-center gap-1">
        <RiseStar />
        {title}
      </span>
    ) : (
      title
    );
  };

  const sectionTitle = getSectionTitle(
    t("studentCard.predictiveScoreGraphs.title"),
  );

  const { title: graphTitle, subtitle: graphSubtitle } =
    getGraphTitleAndSubtitle();

  const chartTitle = getSectionTitle(graphTitle);

  return (
    <div>
      <h3 className="text-[16px] leading-tight font-semibold">
        {sectionTitle}
      </h3>
      <p className="mt-1.5 mb-4 text-sm text-muted-foreground">
        {t("studentCard.predictiveScoreGraphs.subtitle", {
          fname: student?.studentOrganizationStatus?.firstName || "",
        })}
      </p>

      {/* Predictive Score Buttons */}
      <div
        className="mb-4 grid gap-2"
        style={{
          gridTemplateColumns: `repeat(${predictiveButtons.length}, 1fr)`,
        }}
      >
        {predictiveButtons.map((button) => {
          const IconComponent = button.icon;
          const isSelected = selectedButton === button.key;
          const hasValue = button.value !== null && button.value !== undefined;
          const displayValue = hasValue
            ? Math.round(button.value).toString()
            : "--";

          return (
            <Card
              key={button.key}
              role="button"
              aria-label={t(`${button.titleKey}Aria`)}
              className={clsx(
                "h-36 cursor-pointer rounded-lg transition-all hover:shadow-sm",
                isSelected
                  ? "border-2 border-blue-500 bg-secondary"
                  : "border border-border",
              )}
              onClick={() => handleButtonClick(button.key)}
            >
              <CardContent className="flex h-full flex-col items-center justify-center px-4 py-5">
                {/* Title Section with Icon */}
                <div className="flex w-full items-center justify-between gap-1">
                  <div className="text-base leading-5 font-bold">
                    {t(button.titleKey)}
                  </div>
                  <div
                    className={clsx(
                      "flex h-6 w-6 items-center justify-center rounded-full",
                      button.iconStyles,
                    )}
                  >
                    <IconComponent
                      className="h-4 w-4"
                      style={{
                        color:
                          button.key === "apply"
                            ? "#4F46E5"
                            : button.key === "melt"
                              ? "#059669"
                              : "#CA8A04",
                      }}
                    />
                  </div>
                </div>

                {/* Gap */}
                <div className="h-3" />

                {/* Value and Badge Section */}
                <div className="flex w-full items-center gap-2">
                  <div className="text-2xl leading-8 font-bold">
                    {displayValue}
                  </div>
                  {/* Badge only shows when value exists and index exists */}
                  {hasValue && button.index && (
                    <span className="inline-flex rounded-full border border-slate-200 bg-secondary px-2 py-1 text-xs leading-4 font-semibold">
                      {t(
                        getIndexConfig(button.index).translationKey,
                      ).toUpperCase()}
                    </span>
                  )}
                </div>
              </CardContent>
            </Card>
          );
        })}
      </div>

      <LineChart
        data={chartData}
        interval="monthly"
        title={chartTitle}
        description={graphSubtitle}
        className="w-full"
        loading={loading}
        error={Boolean(error)}
        emptyMessage={t("studentCard.predictiveScoreGraphs.emptyMessage", {
          type: selectedButton.toLowerCase(),
        })}
        errorMessage={t("studentCard.predictiveScoreGraphs.errorMessage", {
          type: selectedButton.toLowerCase(),
        })}
        gradient={{
          startColor: "var(--chart-1-gradient)",
          endColor: "var(--chart-1-gradient)",
          startOpacity: isInDarkMode() ? 0.15 : 0.5,
          endOpacity: 0,
        }}
        colors={{
          current: "var(--chart-1)",
        }}
        tooltipContent={(data: TooltipData) => {
          const timeSeriesPoint = timeSeries.find(
            (point) => point.date === data.originalData.period,
          );
          const indexValue = timeSeriesPoint?.index || null;
          const indexConfig = getIndexConfig(indexValue);

          return (
            <div>
              {indexConfig.translationKey && (
                <div
                  className={`mb-1.5 text-xs leading-4 font-bold ${indexConfig.colorClass}`}
                >
                  {t(indexConfig.translationKey)}
                </div>
              )}
              <div className="mb-1.5">
                {t("studentCard.predictiveScoreGraphs.tooltipMessage", {
                  score: data.currentValue,
                })}
              </div>
              <div className="font-medium">
                {standardFormatDateLong(data.originalData.period as string)}
              </div>
            </div>
          );
        }}
        yAxisRange={{ min: 0, max: 100 }}
        xAxisLabelStrategy="progressive"
        menuOptions={[
          {
            label: t("studentCard.predictiveScoreGraphs.actions.exportCSV"),
            onClick: handleExportCSV,
            icon: <FileDown className="h-4 w-4" />,
            disabled: actionsDisabled,
          },
          {
            label: t("studentCard.predictiveScoreGraphs.actions.share"),
            onClick: shareCurrentLink,
            icon: <Share className="h-4 w-4" />,
            disabled: actionsDisabled,
          },
        ]}
      />

      {/* Feature Dialog */}
      <FeatureDialog
        isOpen={showFeatureDialog}
        onOpenChange={setShowFeatureDialog}
        type="feature"
        feature={PartnerFeatureEnum.Rise}
      />
    </div>
  );
};
